/**
 * CalVer release comparison for force-update (`YYYY.MM.DD` only).
 * @see doc/spec/SPEC_force_update_semver.md (Option B)
 */

/** Env default: no floor enforced until policy sets a real release. */
export const CALVER_FLOOR_DISABLED = '0000.00.00'

const CALVER_RELEASE_RE = /^(\d{4})\.(\d{2})\.(\d{2})$/

export type CalVerParts = {
    year: number
    month: number
    day: number
}

export function parseCalVerRelease(version: string): CalVerParts | null {
    const trimmed = version.trim()
    const match = CALVER_RELEASE_RE.exec(trimmed)
    if (!match) {
        return null
    }

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return null
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null
    }

    return { year, month, day }
}

export function isValidCalVerRelease(version: string): boolean {
    return parseCalVerRelease(version) !== null
}

export function isCalVerFloorDisabled(min: string): boolean {
    return min.trim() === CALVER_FLOOR_DISABLED
}

export function formatCalVerParts(parts: CalVerParts): string {
    const month = String(parts.month).padStart(2, '0')
    const day = String(parts.day).padStart(2, '0')
    return `${parts.year}.${month}.${day}`
}

/** True when `current` is the same `YYYY.MM.DD` as `dateBase`. */
export function isSameCalVerDate(current: string | null | undefined, dateBase: string): boolean {
    const trimmed = current?.trim()
    if (!trimmed) {
        return false
    }
    return trimmed === dateBase && isValidCalVerRelease(trimmed)
}

/**
 * @returns negative if a < b, positive if a > b, 0 if equal; null if either string is invalid.
 */
export function compareRelease(a: string, b: string): number | null {
    const left = parseCalVerRelease(a)
    const right = parseCalVerRelease(b)
    if (!left || !right) {
        return null
    }

    if (left.year !== right.year) {
        return left.year < right.year ? -1 : 1
    }
    if (left.month !== right.month) {
        return left.month < right.month ? -1 : 1
    }
    if (left.day !== right.day) {
        return left.day < right.day ? -1 : 1
    }
    return 0
}

export function isReleaseBelow(app: string, min: string): boolean {
    if (isCalVerFloorDisabled(min)) {
        return false
    }
    const cmp = compareRelease(app, min)
    return cmp !== null && cmp < 0
}
